import { makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import type { ICeleriantClient } from '@/services/CeleriantClient';
import type { IAppBus } from '@/services/AppBus';
import type { IDomConnectivity } from '@/services/DomConnectivity';
import type { IDomNavigation } from '@/services/DomNavigation';
import type { ILogger } from '@/services/Logger';
import { DomainError, ServerError } from '@/domain/errors';
import { parseRoute, pathWithoutKeys, resumePath, type Route } from '@/domain/joinLink';
import type { AdminShellStore } from './admin/AdminShellStore';
import type { OnboardingStore } from './onboarding/OnboardingStore';

export interface Toast {
  id: number;
  kind: 'info' | 'success' | 'warn' | 'error';
  message: string;
}

// The root ViewModel: boot, connectivity, the current URL, and toasts. It does NOT decide what this
// device is. That comes from the share code the device redeemed, which is `OnboardingStore`'s job
// (docs/access.md); the shell only decides which URL starts a join and takes the bearer keys back
// out of the address bar afterwards.
//
// P1's `screen` path-switch and `sessionId` dev default are gone, as P1 said they would be. There
// is no router and there will not be one (INVARIANTS rule 12): `parseRoute` is a string match over
// a fragment, tested as a pure function.
//
// No runInAction anywhere in this app. Async methods are flows (the Babel transform in
// vite.config.ts), so post-await mutations are tracked. A callback that fires later, such as a bus
// subscriber, mutates through an action METHOD, which makeAutoObservable has already wrapped.
export class AppStore {
  private _booted = false;
  private _bootError: string | undefined;
  private _online: boolean;
  private _path: string;
  // The session share key of the last join this shell started. A URL change that carries the same
  // key is the address bar settling, not a second scan. Cleared when the join fails, so the code
  // that failed is retryable rather than permanently burned on this device.
  private _attempted: string | undefined;
  private _toasts: Toast[] = [];
  private _nextToastId = 1;

  constructor(
    private readonly celeriant: ICeleriantClient,
    private readonly onboarding: OnboardingStore,
    private readonly admin: AdminShellStore,
    // Not held as a field: the shell only ever subscribes, below. A store that keeps a bus it does
    // not publish on invites a publish that should have been a method call (INVARIANTS rule 8).
    bus: IAppBus,
    private readonly connectivity: IDomConnectivity,
    private readonly nav: IDomNavigation,
    private readonly logger: ILogger,
  ) {
    this._online = connectivity.online;
    this._path = nav.path;

    // Subscribe in the constructor, not in init(): the container is lazy, and a subscription that
    // waits for an async init misses every publish in between (INVARIANTS rule 20). Nothing
    // publishes here, so this is safe to run before the rest of the graph is alive.
    bus.subscribe('TOAST', (t) => this.pushToast(t.kind, t.message));
    bus.subscribe('CONNECTIVITY_CHANGED', (c) => this.applyOnline(c.online));
    bus.subscribe('URL_CHANGED', (u) => this.applyPath(u.path));

    makeAutoObservable(this);
  }

  get booted(): boolean {
    return this._booted;
  }
  get bootError(): string | undefined {
    return this._bootError;
  }
  // grading-day is online-only: every screen gates on this, and the gate has to be a visible
  // state rather than an inference from a write that failed.
  get online(): boolean {
    return this._online;
  }
  // The current URL, fragment included, because that is where a scanned share link carries its
  // keys.
  get path(): string {
    return this._path;
  }

  // What the URL asks for. What the device IS still comes from the registry.
  get route(): Route {
    return parseRoute(this._path);
  }

  get toasts(): readonly Toast[] {
    return this._toasts;
  }

  // Called once from main.tsx, after the container is configured and the boot subscriber check has
  // passed. Starting the Dom* wrappers here rather than in the constructor is what keeps their
  // first publish landing on live handlers, and it is what makes `route` readable below: the
  // initial URL_CHANGED has already been folded by the time the routing runs.
  init = async (): Promise<void> => {
    this.connectivity.start();
    this.nav.start();
    try {
      // The identity keypair and the local store, nothing more. No aggregate is opened at boot any
      // more: which one this device wants is a property of the code it redeemed, and a shell that
      // guessed would open the wrong session before the human had scanned anything.
      await this.celeriant.ready();
      this.finishBoot();
    } catch (cause) {
      // Translate before anything else looks at it. The SDK is foreign code that throws plain
      // Errors, and a bug inside it throws a TypeError; above this line only the taxonomy exists
      // (INVARIANTS rule 17). An untyped catch-all here would let a programmer bug be absorbed as
      // if it were a gateway outage. The cause's message is carried through so the boot screen
      // still names the actual failure.
      const failure =
        cause instanceof DomainError
          ? cause
          : new ServerError(cause instanceof Error ? cause.message : String(cause), { cause });
      this.logger.error('boot failed', failure);
      this.failBoot(failure);
      return;
    }
    await this.follow();
  };

  dismissToast = (id: number): void => {
    this._toasts = this._toasts.filter((t) => t.id !== id);
  };

  // Act on whatever the URL currently says. Idempotent by construction: a join runs once per
  // distinct session key, so the URL_CHANGED that the key-stripping replaceState publishes does not
  // start a second one.
  private follow = async (): Promise<void> => {
    const route = this.route;
    if (route.kind === 'admin') {
      await this.admin.init();
      return;
    }
    // Off the admin route, the admin screen stands its timers down. The laptop that has been
    // running the hour is also the device holding the TV, so it is the one most likely to scan its
    // own QR and land on a coach screen — with the tick pulse still armed, computing move lists for
    // a session nobody on this device is watching (session/scraps.md, the [P6] dispose item).
    // Idempotent, so the URL_CHANGED that the key-stripping replaceState publishes costs nothing.
    this.admin.standDown();
    // The reload of a device that already joined. Skipped when this store's onboarding already
    // holds the same hash: that is the URL settling after the replaceState below, not a reload.
    if (route.kind === 'resume') {
      if (route.params.codeHash === this._attempted) return;
      this.markAttempted(route.params.codeHash);
      try {
        await this.onboarding.resume(route.params);
      } catch {
        // Surfaced on `onboarding.lastError` like a failed join; cleared so a re-scan or a second
        // reload retries rather than finding the hash burned.
        this.clearAttempted();
      }
      return;
    }
    if (route.kind !== 'join' || route.params.sk === this._attempted) return;
    this.markAttempted(route.params.sk);
    try {
      await this.onboarding.join(route.params);
    } catch {
      // Already translated, logged and surfaced on `onboarding.lastError`, which the join screen
      // renders. Re-throwing out of a bus-driven call would reach no catch and no boundary
      // (INVARIANTS rule 17).
      //
      // Clear the marker before returning. It exists to stop the key-stripping replaceState below
      // from starting a SECOND join off its own URL_CHANGED, and a join that failed never reached
      // that line. Leaving it set makes the one code the human is physically holding the one code
      // this device will never try again: OnboardingStore stays usable after a failure on purpose,
      // and the shell would be throwing that away. Re-scanning the same QR now retries.
      this.clearAttempted();
      return;
    }
    // The grant is persistent now, so the keys have done their job. Replace them with the RESUME
    // route: still no key in the address bar and no history entry holding one, but a reload now
    // finds its way back to the role instead of the landing page. The hash is the registry's
    // lookup key, not a credential — see `resumePath` in joinLink.ts. The marker moves to the
    // hash so the URL_CHANGED this replace publishes is recognised as the bar settling, and the
    // guard in the resume branch above makes even a missed marker inert.
    const codeHash = this.onboarding.codeHash;
    if (codeHash !== undefined) {
      this.markAttempted(codeHash);
      this.nav.replace(resumePath({ projectId: route.params.projectId, codeHash }));
    } else {
      // Unreachable on a successful join today; kept so a future join path that forgot the hash
      // degrades to the old key-strip rather than leaving keys in the bar.
      this.nav.replace(pathWithoutKeys(this._path));
    }
  };

  // Actions. makeAutoObservable wraps every method, so a later-firing bus handler that calls one
  // of these is already batched, which is why this store has no runInAction.
  private applyOnline(online: boolean): void {
    this._online = online;
  }

  private applyPath(path: string): void {
    this._path = path;
    // Fire and forget: this is a bus handler, and the outcome lands on observable state either way.
    if (this._booted) void this.follow();
  }

  private markAttempted(sk: string): void {
    this._attempted = sk;
  }

  private clearAttempted(): void {
    this._attempted = undefined;
  }

  private finishBoot(): void {
    this._booted = true;
  }

  // Takes a DomainError, not `unknown`: the translation is the caller's job and the signature is
  // what keeps it from being skipped.
  private failBoot(failure: DomainError): void {
    this._bootError = failure.message;
  }

  private pushToast(kind: Toast['kind'], message: string): void {
    this._toasts = [...this._toasts, { id: this._nextToastId++, kind, message }];
  }
}

export const TOKEN_AppStore = token<AppStore>('AppStore');
